import type { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';

/** Shopify's consent param on a checkout URL. Pass it through unchanged. */
export const SHOPIFY_CONSENT_PARAM = '_cs';

const TIMEOUT_MS = 4000;

/**
 * A fresh checkout URL that carries the visitor's current consent. One retry,
 * then `fallback`, so checkout is never blocked; without `_cs` Shopify falls
 * back to its admin privacy settings.
 */
export async function fetchConsentCheckoutUrl(
  store: ShopifyStore,
  cartId: string,
  fallback: string,
): Promise<string> {
  return (
    (await requestConsentCheckoutUrl(store, cartId)) ??
    (await requestConsentCheckoutUrl(store, cartId)) ??
    fallback
  );
}

async function requestConsentCheckoutUrl(
  store: ShopifyStore,
  cartId: string,
): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`/api/cart/checkout/?store=${store}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cartId }),
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { checkoutUrl?: unknown };
    return typeof data.checkoutUrl === 'string' ? data.checkoutUrl : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
